/**
 * Wraps a promise with a timeout that resolves with a fallback value if the original promise doesn't resolve in time
 * @param {Promise} promise - The promise to wrap
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {*} fallbackValue - Value to resolve with on timeout
 * @returns {Promise} The wrapped promise
 */
export const withTimeout = (promise, timeoutMs, fallbackValue) => {
  return Promise.race([
    promise,
    new Promise((resolve) => {
      setTimeout(() => {
        console.warn(`Promise timeout after ${timeoutMs}ms - using fallback value`);
        resolve(fallbackValue);
      }, timeoutMs);
    }),
  ]);
};

/**
 * Wraps a promise with development mode detection that immediately resolves with a default value in development mode
 * @template T
 * @param {Promise<T>} promise - The promise to wrap
 * @param {T} devModeValue - Value to resolve with in development mode
 * @returns {Promise<T>} The wrapped promise or immediate resolution in development mode
 */
export const withDevModeFailover = (promise, devModeValue) => {
  if (process.env.NODE_ENV === "development") {
    console.info("Dev Mode detected - using default values");
    return Promise.resolve({ ...devModeValue, reason: "dev-mode" });
  }

  return promise;
};

/**
 * Wraps a promise with ad zone detection that immediately resolves with a default value if no ad zones are detected in the DOM
 * @template T
 * @param {Promise<T>} promise - The promise to wrap
 * @param {T} noAdsValue - Value to resolve with in no ads detected case
 */
export const withNoAdsFailover = (promise, noAdsValue) => {
  const adZones = document.querySelectorAll(".adsbygoogle-lazy, .adsbygoogle");
  const fallback = { ads: false, analytics: false, ...(noAdsValue || {}) };

  if (adZones.length === 0) {
    if (process.env.NODE_ENV === "development") {
      console.info("No ad zones detected - skipping ad loading");
    }
    return Promise.resolve({ ...fallback, reason: "no-ad-zones" });
  }

  return promise;
};

export const randomUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const logError = window.__sentry ? window.__sentry.logError : () => {};

/**
 * Loads the lunr search library if not already loaded
 */
const loadLunrScript = async () => {
  if (!window.__LunrSearch) {
    if (!window.__loadLunrScriptPromise) {
      window.__loadLunrScriptPromise = new Promise((resolve, reject) => {
        const lunrScript = document.createElement("script");
        lunrScript.onload = () => {
          resolve(window.__LunrSearch);
        };
        lunrScript.onerror = () => {
          reject(new Error("Failed to load Lunr search script"));
        };
        document.querySelector("body").appendChild(lunrScript);
        lunrScript.src = document.getElementById("__lunr_script_preload").href;
      });
    }
    return window.__loadLunrScriptPromise;
  }

  return Promise.resolve(window.__LunrSearch);
};

export const loadLunrSearch = async (options = {}) => {
  const LunrSearchConstructor = await loadLunrScript();
  return new Promise((resolve) => {
    LunrSearchConstructor({
      ...options,
      onLoad: (loadedLunrSearch) => resolve(loadedLunrSearch),
    });
  });
};

/**
 *
 * @param {HTMLElement} element
 * @param {string} transitionProperty
 * @param {number} transitionDuration
 * @returns {Promise<void>}
 */
export const onTransitionEnd = (element, transitionProperty, transitionDuration) => {
  return new Promise((resolve) => {
    let transitionFallback;

    // Only respond to opacity transition on the backdrop itself
    const handleTransitionEnd = (e) => {
      if (e.target === element && e.propertyName === transitionProperty) {
        window.clearTimeout(transitionFallback);
        element.removeEventListener("transitionend", handleTransitionEnd);
        resolve();
      }
    };

    element.addEventListener("transitionend", handleTransitionEnd);

    // Fallback in case transition doesn't fire
    transitionFallback = setTimeout(() => {
      element.removeEventListener("transitionend", handleTransitionEnd);
      resolve();
    }, transitionDuration + 50);
  });
};

export const postJSON = async (url, data) => {
  return fetchJSON("POST", url, data);
};

export const getJSON = async (url) => {
  return fetchJSON("GET", url);
};

export const fetchJSON = async (method, url, data) => {
  const params = {
    method: method,
    // mode: 'cors',
    cache: "no-cache",
    // credentials: 'same-origin',
    headers: {
      "Content-Type": "application/json",
    },
    redirect: "follow",
    // referrerPolicy: 'no-referrer-when-downgrade',
  };
  if (data) {
    params.body = JSON.stringify(data);
  }
  const response = await fetch(url, params);
  const json = await response.text();
  return JSON.parse(json);
};

/**
 * Safe storage wrapper that handles localStorage restrictions
 * Falls back to cookies when localStorage is blocked (private browsing, iframes, etc.)
 */
export const safeStorage = {
  /**
   * Get item from storage
   * @param {string} key
   * @returns {string|null}
   */
  getItem(key) {
    try {
      // Try localStorage first
      return localStorage.getItem(key);
    } catch {
      // Fallback to cookies
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [cookieKey, cookieValue] = cookie.trim().split("=");
        if (cookieKey === key) {
          return decodeURIComponent(cookieValue);
        }
      }
      return null;
    }
  },

  /**
   * Set item in storage
   * @param {string} key
   * @param {string} value
   */
  setItem(key, value) {
    try {
      // Try localStorage first
      localStorage.setItem(key, value);
    } catch {
      // Fallback to cookies (expires in 1 year)
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    }
  },
};
